/**
 * SC-255 — the message shown when zoom has pushed in-game controls out of the viewport.
 *
 * One sentence, and the opening clause is identical everywhere. Only the *fix* differs, and only
 * where it has to: a phone has no ⌘ or Ctrl key to press. Deliberately **not** varied by which
 * detection arm fired — an earlier revision had a separate "try making the window wider" wording
 * for the case where zoom could not be proven, which split hairs the user does not care about.
 *
 * Kept pure and separate from the component so it can be unit-tested: the platforms that matter
 * most here are the ones hardest to reach in a test run.
 */

export interface ZoomHintNavigatorLike {
  userAgent: string
  maxTouchPoints: number
}

/**
 * Is this a touch device with no keyboard to press the shortcut on?
 *
 * `maxTouchPoints` rather than the user agent alone, because an iPad in Safari's desktop mode
 * reports itself as a Macintosh — matching on the UA string would send every iPad the ⌘+0 hint
 * for a key it does not have. Real Macs report zero touch points, so the pair separates them.
 *
 * A Windows or ChromeOS touchscreen laptop has both, and keeps the keyboard hint: it is the more
 * precise instruction, and pinching works there anyway.
 */
export function isTouchOnlyDevice(nav: ZoomHintNavigatorLike): boolean {
  if (nav.maxTouchPoints <= 0) return false
  return /iPhone|iPad|iPod|Android/.test(nav.userAgent) || /Mac/.test(nav.userAgent)
}

/** Apple keyboards label the modifier ⌘; everything else uses Ctrl. */
function isAppleKeyboard(nav: ZoomHintNavigatorLike): boolean {
  return /Mac|iPhone|iPad|iPod/.test(nav.userAgent)
}

/** The reset shortcut as the user's own keyboard labels it. */
export function zoomResetShortcut(nav: ZoomHintNavigatorLike): string {
  return isAppleKeyboard(nav) ? '⌘+0' : 'Ctrl+0'
}

/** Shared opening clause, so the two variants cannot drift apart. */
const OPENING = "You're zoomed in, some controls are off-screen."

/**
 * The message body.
 *
 * Anything with a keyboard is told to reset — reset only, no zoom-out step, because one
 * instruction that lands the user back at 100% beats two that make them choose. A touch-only
 * device is told to pinch instead, and is shown no keys at all.
 */
export function zoomIndicatorMessage(nav: ZoomHintNavigatorLike): string {
  if (isTouchOnlyDevice(nav)) {
    return `${OPENING} Pinch to zoom out and bring them back.`
  }
  return `${OPENING} Press ${zoomResetShortcut(nav)} to get back to 100%.`
}
